import { z } from 'zod';

import type { AttendanceDay, AttendanceStatus } from '../records';

const uuid = z.string().uuid();

/**
 * A calendar day as `YYYY-MM-DD` that exists (no 2027-02-30).
 */
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Not a calendar day');

/**
 * Days between two `YYYY-MM-DD` dates, both included.
 */
const spanDays = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

/**
 * The longest range one excuse may cover.
 */
export const MAX_EXCUSE_DAYS = 60;

/**
 * The four marks, as a schema.
 */
export const attendanceStatusSchema = z.enum(['present', 'absent', 'late', 'excused']);

/**
 * Body of `PUT /school/attendance` (scoping 3.7, #105): the marks of one group on one day, or in
 * one session when `blockId` names a timetable block of the group on that weekday.
 * `others: 'present'` is the bulk action: every other active student of the group is marked
 * present. Idempotent: sending the same body again changes nothing.
 */
export const attendanceMarksInputSchema = z
  .object({
    groupId: uuid,
    date: day,
    blockId: uuid.nullable().optional(),
    marks: z
      .array(
        z.object({
          studentId: uuid,
          status: attendanceStatusSchema,
          note: z.string().trim().max(500).nullable().optional(),
        }),
      )
      .max(200)
      .default([]),
    others: z.literal('present').optional(),
  })
  .refine((input) => input.marks.length > 0 || input.others !== undefined, {
    path: ['marks'],
    message: 'Give at least one mark or others: present',
  })
  .refine(
    (input) => new Set(input.marks.map((mark) => mark.studentId)).size === input.marks.length,
    {
      path: ['marks'],
      message: 'A student appears twice',
    },
  );

/**
 * Query of `GET /school/attendance`: a group's roll on one day, or in one session.
 */
export const attendanceRollQuerySchema = z.object({
  groupId: uuid,
  date: day,
  blockId: uuid.optional(),
});

/**
 * Query of `GET /students/{id}/attendance`: a period, or a date range; by default the current
 * school year. A range covers at most 400 days.
 */
export const studentAttendanceQuerySchema = z
  .object({ periodId: uuid.optional(), from: day.optional(), to: day.optional() })
  .refine((query) => !query.periodId || (!query.from && !query.to), {
    path: ['periodId'],
    message: 'Give a period or a range, not both',
  })
  .refine((query) => !query.from || !query.to || query.from <= query.to, {
    path: ['to'],
    message: 'to must not be before from',
  })
  .refine((query) => !query.from || !query.to || spanDays(query.from, query.to) <= 400, {
    path: ['to'],
    message: 'At most 400 days',
  });

/**
 * Query of `GET /school/attendance/summary`: a group in a period, optionally one subject's
 * sessions.
 */
export const attendanceSummaryQuerySchema = z.object({
  periodId: uuid,
  groupId: uuid,
  subjectId: uuid.optional(),
});

/**
 * Body of `POST /students/{id}/excuses`: a guardian's excuse for a linked child over a date
 * range, with an optional file uploaded with purpose `excuse`.
 */
export const createExcuseInputSchema = z
  .object({
    startsOn: day,
    endsOn: day,
    reason: z.string().trim().min(3).max(1000),
    attachment: z
      .object({
        key: z.string().min(1).max(500),
        filename: z.string().trim().min(1).max(200),
      })
      .optional(),
  })
  .refine((input) => input.startsOn <= input.endsOn, {
    path: ['endsOn'],
    message: 'endsOn must not be before startsOn',
  })
  .refine((input) => spanDays(input.startsOn, input.endsOn) <= MAX_EXCUSE_DAYS, {
    path: ['endsOn'],
    message: `An excuse covers at most ${MAX_EXCUSE_DAYS} days`,
  });

/**
 * The states of an excuse.
 */
export const excuseStatusSchema = z.enum(['pending', 'approved', 'rejected']);

/**
 * Query of `GET /school/attendance/excuses` (the school's review queue) and of
 * `GET /students/{id}/excuses` (which ignores `studentId`).
 */
export const excusesQuerySchema = z.object({
  status: excuseStatusSchema.optional(),
  studentId: uuid.optional(),
});

/**
 * Body of `POST /school/attendance/excuses/{id}/review`.
 */
export const reviewExcuseInputSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  note: z.string().trim().max(500).optional(),
});

/**
 * Body of `PUT /school/attendance/settings`: the school's threshold.
 */
export const attendanceSettingsInputSchema = z.object({
  absenceThresholdPercent: z.number().min(1).max(100),
  minimumMarks: z.number().int().min(1).max(500),
});

/**
 * Query of `GET /school/attendance/alerts`.
 */
export const attendanceAlertsQuerySchema = z.object({
  periodId: uuid.optional(),
  groupId: uuid.optional(),
});

/**
 * Body of `PUT /school/attendance`.
 */
export type AttendanceMarksInput = z.input<typeof attendanceMarksInputSchema>;

/**
 * Body of `POST /students/{id}/excuses`.
 */
export type CreateExcuseInput = z.input<typeof createExcuseInputSchema>;

/**
 * Body of `POST /school/attendance/excuses/{id}/review`.
 */
export type ReviewExcuseInput = z.input<typeof reviewExcuseInputSchema>;

/**
 * Body of `PUT /school/attendance/settings`.
 */
export type AttendanceSettingsInput = z.input<typeof attendanceSettingsInputSchema>;

/**
 * State of an excuse.
 */
export type ExcuseStatus = z.infer<typeof excuseStatusSchema>;

/**
 * Answer of `PUT /school/attendance`: the roll after the write, and how many marks were created
 * or changed (0 when the request was a replay).
 */
export type AttendanceMarkResult = AttendanceDay & { changed: number };

/**
 * Counts of marks with the percentages: attendance counts present, late and excused; absence
 * counts unexcused absences only. Percentages are null without marks.
 */
export type AttendanceCounts = Record<AttendanceStatus, number> & {
  marks: number;
  attendancePercent: number | null;
  absencePercent: number | null;
};

/**
 * One stored mark as a student's history shows it.
 */
export type AttendanceMark = {
  id: string;
  date: string;
  groupId: string;
  /**
   * The timetable block of a session mark; null for a day mark (or once the block is deleted).
   */
  blockId: string | null;
  /**
   * `day`, or the session's block id.
   */
  slot: string;
  subjectId: string | null;
  subjectName: string | null;
  status: AttendanceStatus;
  note: string | null;
  excuseId: string | null;
  updatedAt: string;
};

/**
 * Counts of one subject's session marks (`subjectId` null gathers the day marks).
 */
export type SubjectAttendance = {
  subjectId: string | null;
  subjectName: string | null;
  counts: AttendanceCounts;
};

/**
 * Answer of `GET /students/{id}/attendance`: one student's marks in a period or range, with
 * totals and percentages overall and per subject.
 */
export type StudentAttendance = {
  student: { id: string; givenName: string | null; familyName: string | null };
  period: { id: string; name: string; startsOn: string; endsOn: string } | null;
  from: string;
  to: string;
  records: AttendanceMark[];
  totals: AttendanceCounts;
  subjects: SubjectAttendance[];
};

/**
 * Answer of `GET /school/attendance/summary`: percentages in a period per student of a group,
 * overall (or of one subject's sessions when `subjectId` is set) and per subject.
 */
export type AttendanceSummary = {
  period: { id: string; name: string; startsOn: string; endsOn: string };
  groupId: string;
  subjectId: string | null;
  threshold: AttendanceSettings;
  students: {
    studentId: string;
    givenName: string | null;
    familyName: string | null;
    /**
     * A short-lived link to the student's photo, or null without one.
     */
    photoUrl: string | null;
    overall: AttendanceCounts;
    subjects: SubjectAttendance[];
    /**
     * Whether the school was alerted about this student in this period.
     */
    alerted: boolean;
  }[];
};

/**
 * An excuse as the school and the family see it.
 */
export type AttendanceExcuse = {
  id: string;
  studentId: string;
  studentName: string | null;
  startsOn: string;
  endsOn: string;
  reason: string;
  attachment: { key: string; filename: string } | null;
  status: ExcuseStatus;
  submittedById: string | null;
  submittedByName: string | null;
  submittedAt: string;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
};

/**
 * Answer of the review: the excuse and how many absences it turned into excused.
 */
export type ExcuseReviewResult = AttendanceExcuse & { excusedMarks: number };

/**
 * The school's threshold; `isDefault` while the school has not set its own.
 */
export type AttendanceSettings = {
  absenceThresholdPercent: number;
  minimumMarks: number;
  isDefault: boolean;
};

/**
 * An alert raised to the school: a student reached the threshold in a period.
 */
export type AttendanceAlert = {
  id: string;
  studentId: string;
  studentName: string | null;
  groupId: string | null;
  groupName: string | null;
  periodId: string;
  periodName: string;
  absencePercent: number;
  thresholdPercent: number;
  marks: number;
  absences: number;
  createdAt: string;
};
